import React, { useState, useRef, useMemo } from 'react';
import Draggable from 'react-draggable';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, IconButton, Tooltip, TablePagination, TextField, TableSortLabel
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import DescriptionIcon from '@mui/icons-material/Description';
import LockIcon from '@mui/icons-material/Lock';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { calculateVatAmount } from '../../../Utils/GetVatValue';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import CustomMonthYearIndividualDatePicker from '../../Widgets/CustomDates/CustomMonthYearIndividualDatePicker/CustomMonthYearIndividualDatePicker';
import CustomYearDatePicker from '../../Widgets/CustomDates/CustomYearDatePicker/CustomYearDatePicker';

function descendingComparator(a, b, orderBy) {
    if (a[orderBy] < b[orderBy]) {
        return 1;
    }
    if (a[orderBy] > b[orderBy]) {
        return -1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

const SmallTransactionViewer = ({
    transactions = [],
    open = false,
    onClose = () => { },
    viewDocument,
    handleAssignDocument,
    assets,
    startDate,
    endDate,
    setStartDate,
    setEndDate
}) => {
    const { t } = useTranslation();
    const nodeRef = useRef(null);

    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Sorting
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('');

    // Search
    const [searchTerm, setSearchTerm] = useState('');

    // ========== Pagination Handlers ==========
    const handleChangePage = (_, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // ========== Sorting Handlers ==========
    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    // ========== Date Handlers ==========
    const handleStartDateChange = (newValue, type) => {
        setStartDate(newValue, type);
    };
    const handleEndDateChangeNew = (newValue, type) => {
        setEndDate(newValue, type);
    };

    // ========== Search Handler ==========
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setPage(0); // reset to first page on a new search
    };

    // Build a new array that includes participatingInDepreciation, isLocked, vatAmount, netAmount
    const enrichedTransactions = useMemo(() => {
        return transactions.map((tx) => {
            const assignedAsset = assets.find(
                (asset) => asset.expenseId === tx.id || asset.journalEntryId === tx.id
            );
            const vatAmount = calculateVatAmount(tx.amount, tx.vat, tx.documentDate);
            const netAmount = (tx.amount || 0) - vatAmount;
            return {
                ...tx,
                participatingInDepreciation: assignedAsset ? 'Yes' : 'No',
                isLocked: !!tx.isLocked,
                vatAmount,
                netAmount,
            };
        });
    }, [transactions, assets]);

    // Filter by search term
    const filteredTransactions = useMemo(() => {
        if (!searchTerm) return enrichedTransactions;

        const searchString = searchTerm.toLowerCase();
        return enrichedTransactions.filter((tx) => {
            const docDateString = tx.documentDate
                ? new Date(tx.documentDate).toLocaleDateString().toLowerCase()
                : '';
            const typeLabel = tx.type === 1 ? 'expense' : 'journalentry';

            const lockedLabel = tx.isLocked ? 'locked' : 'unlocked';
            return (
                (tx.description?.toLowerCase() || '').includes(searchString) ||
                (tx.reference?.toLowerCase() || '').includes(searchString) ||
                (tx.participatingInDepreciation?.toLowerCase() || '').includes(searchString) ||
                (tx.amount?.toString().toLowerCase() || '').includes(searchString) ||
                (tx.netAmount?.toString().toLowerCase() || '').includes(searchString) ||
                (tx.vatAmount?.toString().toLowerCase() || '').includes(searchString) ||
                docDateString.includes(searchString) ||
                typeLabel.includes(searchString) ||
                lockedLabel.includes(searchString)
            );
        });
    }, [enrichedTransactions, searchTerm]);

    // Sort after filtering
    const sortedTransactions = useMemo(() => {
        // If no column is selected for sorting, simply return the filtered array
        if (!orderBy) return filteredTransactions;
        return stableSort(filteredTransactions, getComparator(order, orderBy));
    }, [filteredTransactions, order, orderBy]);

    // Apply pagination
    const paginatedTransactions = useMemo(() => {
        return sortedTransactions.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
    }, [sortedTransactions, page, rowsPerPage]);

    if (!open) return null;

    return (
        <Draggable handle=".drag-handle" nodeRef={nodeRef}>
            <div
                ref={nodeRef}
                style={{
                    position: 'absolute',
                    top: '15%',
                    left: '30%',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    padding: '16px',
                    minWidth: '900px',
                    maxHeight: '80vh',
                    overflowY: 'auto',
                    zIndex: 1300,
                    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.4)',
                    border: '1px solid #304FFF'
                }}
            >
                {/* ======= Header ======= */}
                <div
                    className="drag-handle"
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'move',
                        marginBottom: 8
                    }}
                >
                    <Tooltip title={t('Close')}>
                        <IconButton onClick={onClose}>
                            <CancelIcon style={{ color: '#304FFF' }} />
                        </IconButton>
                    </Tooltip>
                    <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                        {t('EquipmentPurchaseTransactions')}
                    </div>
                </div>

                {/* ======= Date Picker Row ======= */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginBottom: '16px' }}>
                    <TextField
                        placeholder={t('Search')}
                        value={searchTerm}
                        onChange={handleSearchChange}
                        variant="standard"

                        InputProps={{
                            disableUnderline: true,
                            className: 'custom-input-box-sizing-toggle',
                            sx: {
                                height: '48px',
                                width: '250px',
                                padding: '0 10px',
                                borderRadius: '8px',
                                background: '#F5F5F6',
                                border: '1px solid transparent',
                                '&:focus-within': {
                                    border: '2px solid var(--Foundation-Blue-Normal, #304FFF)',
                                },
                            },
                        }}
                        inputProps={{
                            style: {
                                textAlign: 'right',
                                direction: 'rtl',
                            },
                        }}
                    />
                    <div >
                        <CustomYearDatePicker
                            onDateChange={(date) => handleStartDateChange(date)}
                            date={startDate}
                            label={t("Date")}
                        />
                    </div>

                    {/* Optional End Date (if needed) */}
                    {/* <CustomMonthYearIndividualDatePicker
                        onDateChange={(date) => handleEndDateChangeNew(date)}
                        date={dayjs(endDate)}
                        label={t("EndDate")}
                    /> */}
                </div>


                {/* ======= Table ======= */}
                <TableContainer component={Paper} style={{ maxHeight: '300px' }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                {/* Blank cell for doc icon */}
                                <TableCell />

                                <TableCell sortDirection={orderBy === 'description' ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === 'description'}
                                        direction={orderBy === 'description' ? order : 'asc'}
                                        onClick={() => handleRequestSort('description')}
                                    >
                                        {t('Description')}
                                    </TableSortLabel>
                                </TableCell>

                                <TableCell sortDirection={orderBy === 'reference' ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === 'reference'}
                                        direction={orderBy === 'reference' ? order : 'asc'}
                                        onClick={() => handleRequestSort('reference')}
                                    >
                                        {t('Reference')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={orderBy === 'amount' ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === 'amount'}
                                        direction={orderBy === 'amount' ? order : 'asc'}
                                        onClick={() => handleRequestSort('amount')}
                                    >
                                        {t('Amount')}
                                    </TableSortLabel>
                                </TableCell>

                                <TableCell sortDirection={orderBy === 'vatAmount' ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === 'vatAmount'}
                                        direction={orderBy === 'vatAmount' ? order : 'asc'}
                                        onClick={() => handleRequestSort('vatAmount')}
                                    >
                                        {t('PCNVatAmount')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={orderBy === 'netAmount' ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === 'netAmount'}
                                        direction={orderBy === 'netAmount' ? order : 'asc'}
                                        onClick={() => handleRequestSort('netAmount')}
                                    >
                                        {t('NetAmount')}
                                    </TableSortLabel>
                                </TableCell>


                              

                                <TableCell sortDirection={orderBy === 'documentDate' ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === 'documentDate'}
                                        direction={orderBy === 'documentDate' ? order : 'asc'}
                                        onClick={() => handleRequestSort('documentDate')}
                                    >
                                        {t('documentDate')}
                                    </TableSortLabel>
                                </TableCell>

                                <TableCell sortDirection={orderBy === 'type' ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === 'type'}
                                        direction={orderBy === 'type' ? order : 'asc'}
                                        onClick={() => handleRequestSort('type')}
                                    >
                                        {t('Type')}
                                    </TableSortLabel>
                                </TableCell>

                                <TableCell sortDirection={orderBy === 'participatingInDepreciation' ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === 'participatingInDepreciation'}
                                        direction={orderBy === 'participatingInDepreciation' ? order : 'asc'}
                                        onClick={() => handleRequestSort('participatingInDepreciation')}
                                    >
                                        {t('ParticipatingInDepreciation')}
                                    </TableSortLabel>
                                </TableCell>

                                <TableCell sortDirection={orderBy === 'isLocked' ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === 'isLocked'}
                                        direction={orderBy === 'isLocked' ? order : 'asc'}
                                        onClick={() => handleRequestSort('isLocked')}
                                    >
                                        {t('Locked')}
                                    </TableSortLabel>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedTransactions.map((tx) => {
                                return (
                                    <Tooltip
                                        key={tx.id}
                                        title={
                                            tx.participatingInDepreciation === 'Yes'
                                                ? t('TransactionIsAssignedToAsset')
                                                : ''
                                        }
                                        arrow
                                    >
                                        <TableRow
                                            hover
                                            style={{
                                                cursor: 'pointer',
                                                backgroundColor:
                                                    tx.participatingInDepreciation === 'Yes'
                                                        ? '#D4EDDA'
                                                        : 'inherit'
                                            }}
                                            onClick={() => handleAssignDocument(tx)}
                                        >
                                            <TableCell align="center">
                                                <Tooltip title={t('ViewDocument')}>
                                                    <IconButton
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            viewDocument(tx.id, tx.type);
                                                        }}
                                                    >
                                                        <DescriptionIcon style={{ color: '#304FFF' }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>

                                            <TableCell align="center">{tx.description || '-'}</TableCell>
                                            <TableCell align="center">{tx.reference || '-'}</TableCell>
                                            <TableCell align="center">{beautifyNumber(tx.amount || 0)}</TableCell>
                                            <TableCell align="center">{beautifyNumber(tx.vatAmount)}</TableCell>
                                            <TableCell align="center">{beautifyNumber(tx.netAmount)}</TableCell>
                                            <TableCell align="center">
                                                {tx.documentDate
                                                    ? new Date(tx.documentDate).toLocaleDateString()
                                                    : '-'}
                                            </TableCell>
                                            <TableCell align="center">
                                                {tx.type === 1 ? t('Expense') : t('JournalEntry')}
                                            </TableCell>
                                            <TableCell align="center">
                                                {t(tx.participatingInDepreciation)}
                                            </TableCell>
                                            <TableCell align="center">
                                                {tx.isLocked ? (
                                                    <Tooltip title={t('Locked')}>
                                                        <LockIcon style={{ color: '#6c757d', fontSize: '20px' }} />
                                                    </Tooltip>
                                                ) : (
                                                    '-'
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    </Tooltip>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* ======= Pagination ======= */}
                <TablePagination
                    component="div"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    count={sortedTransactions.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage={t('RowsPerPage')}
                />
            </div>
        </Draggable>
    );
};

export default SmallTransactionViewer;
